import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Input,
  Loading,
  PageHeader,
  StatCard,
} from '@/components/ui';
import { formatDateTime, formatMoney, formatNumber } from '@/lib/format';

/**
 * What the bot has already said, and what not saying it again has saved.
 *
 * A screener call is the same conversation every time — read any two
 * transcripts and only the customer's half differs. Speech is about three
 * quarters of what a call costs, so this page is where most of the margin on a
 * high-volume bot actually comes from.
 *
 * It is also where a mispronunciation gets fixed. Search for the line, listen
 * to it, delete it, and the next call buys a new one.
 */

interface CacheEntry {
  cache_key: string;
  tenant_id: string | null;
  client_name: string | null;
  provider: string;
  voice_id: string;
  model: string;
  sample_rate: number;
  text: string;
  characters: number;
  bytes: number;
  duration_ms: number;
  static: boolean;
  hits: number;
  last_used_at: string | null;
  created_at: string;
}

interface ProviderSaving {
  provider: string;
  entries: number;
  hits: number;
  characters_saved: number;
  cost_saved: number;
}

interface CacheStats {
  entries: number;
  static_entries: number;
  hits_recorded: number;
  characters_saved: number;
  tts_calls_avoided: number;
  cost_saved: number;
  by_provider: ProviderSaving[];
  this_process: {
    hits: number;
    misses: number;
    hit_rate: number;
    disk_files: number;
    disk_bytes: number;
    directory: string;
  };
  pending_hits: number;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function VoiceCache() {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [playing, setPlaying] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const stats = useQuery({
    queryKey: ['tts-cache-stats'],
    queryFn: () => api.get<CacheStats>('/admin/tts-cache/stats'),
    refetchInterval: 30_000,
  });

  const entries = useQuery({
    queryKey: ['tts-cache', query],
    queryFn: () =>
      api.get<{ data: CacheEntry[]; total: number }>('/admin/tts-cache', {
        q: query || undefined,
        page_size: 100,
      }),
  });

  const remove = useMutation({
    mutationFn: (key: string) => api.delete(`/admin/tts-cache/${key}`),
    onSuccess: () => {
      setNote('Deleted. The next call will make a fresh one.');
      queryClient.invalidateQueries({ queryKey: ['tts-cache'] });
      queryClient.invalidateQueries({ queryKey: ['tts-cache-stats'] });
    },
  });

  const regenerate = useMutation({
    mutationFn: (key: string) => api.post(`/admin/tts-cache/${key}/regenerate`),
    onSuccess: () => {
      setNote('Bought again. No caller waited for it.');
      queryClient.invalidateQueries({ queryKey: ['tts-cache'] });
    },
  });

  /**
   * Empty the whole cache.
   *
   * Destructive, and it asks first — but destructive in the mildest sense
   * available here. The cache is a copy: every line in it can be bought again,
   * and the next call that needs one pays for it. What this actually costs is
   * a vendor bill and a little latency on the calls that follow.
   *
   * It exists because the alternative is worse. An operator who has changed a
   * voice, switched provider, or corrected a line the bot was saying wrong
   * otherwise deletes entries one at a time, or reaches for `rm -rf` on a path
   * they have to go and look up.
   */
  const clearAll = useMutation({
    mutationFn: () =>
      api.delete<{ entries: number; files: number }>('/admin/tts-cache'),
    onSuccess: (result) => {
      setConfirming(false);
      setNote(
        `Cleared ${formatNumber(result.entries)} lines. The next call on each ` +
          'will buy it again.',
      );
      queryClient.invalidateQueries({ queryKey: ['tts-cache'] });
      queryClient.invalidateQueries({ queryKey: ['tts-cache-stats'] });
    },
  });

  /** Play a cached line. The server puts a WAV header on the stored samples. */
  async function play(key: string) {
    setPlaying(key);
    try {
      const blob = await api.getBlob(`/admin/tts-cache/${key}/audio`);
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => {
        URL.revokeObjectURL(url);
        setPlaying(null);
      };
      await audio.play();
    } catch {
      setNote('That line could not be played — the file may have been trimmed.');
      setPlaying(null);
    }
  }

  const saving = stats.data;

  return (
    <div>
      <PageHeader
        title="Voice cache"
        description="Every line the bot has said, kept so it is never bought twice. Shared by every client on the same provider account."
        actions={
          <Button
            variant="danger"
            onClick={() => setConfirming(true)}
            disabled={!stats.data?.entries}
          >
            Delete all cached voices
          </Button>
        }
      />

      <ConfirmDialog
        open={confirming}
        title="Empty the voice cache?"
        confirmLabel="Delete everything"
        requireTyping="DELETE"
        busy={clearAll.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => clearAll.mutate()}
        body={
          <>
            <p>
              This removes{' '}
              <strong>{formatNumber(stats.data?.entries ?? 0)} cached lines</strong>{' '}
              for every client on the platform, and the audio files behind them.
            </p>
            <p>
              Nothing is lost permanently — the cache is a copy. Every line can
              be bought again, and the next call that needs one will. What it
              costs is a vendor bill and a slightly slower first call on each
              line until the cache fills back up.
            </p>
            <p className="rounded-md bg-amber-50 p-2.5 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              Warm the common lines again afterwards with{' '}
              <code className="font-mono">glee-warm-cache</code>, or the first
              caller of the day pays the latency on the greeting.
            </p>
          </>
        }
      />

      {note && (
        <div className="mb-4 rounded-md bg-sunken p-3 text-sm text-ink-700 dark:text-ink-200">
          {note}
        </div>
      )}

      {saving && (
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Vendor calls avoided"
            value={formatNumber(saving.tts_calls_avoided)}
            sub="Each one is a synthesis nobody paid for."
          />
          <StatCard
            label="Estimated saving"
            value={formatMoney(saving.cost_saved)}
            sub="Characters never bought, at what we pay for them."
          />
          <StatCard
            label="Lines cached"
            value={formatNumber(saving.entries)}
            sub={`${formatNumber(saving.static_entries)} pre-generated`}
          />
          <StatCard
            label="Hit rate, this process"
            value={`${Math.round((saving.this_process.hit_rate ?? 0) * 100)}%`}
            sub={`${formatNumber(saving.this_process.hits)} hits · ${formatNumber(
              saving.this_process.misses,
            )} misses`}
          />
        </div>
      )}

      {saving && saving.by_provider.length > 0 && (
        <Card
          title="Saving by provider"
          description="Priced separately: they differ by a factor of four, and an average would be true of nobody."
          className="mb-6"
        >
          <div className="overflow-x-auto">
            <table className="glee-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th className="text-right">Lines</th>
                  <th className="text-right">Plays saved</th>
                  <th className="text-right">Characters</th>
                  <th className="text-right">Saved</th>
                </tr>
              </thead>
              <tbody>
                {saving.by_provider.map((row) => (
                  <tr key={row.provider}>
                    <td>{row.provider}</td>
                    <td className="text-right tabular-nums">
                      {formatNumber(row.entries)}
                    </td>
                    <td className="text-right tabular-nums">
                      {formatNumber(row.hits)}
                    </td>
                    <td className="text-right tabular-nums">
                      {formatNumber(row.characters_saved)}
                    </td>
                    <td className="text-right tabular-nums">
                      {formatMoney(row.cost_saved)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-ink-500">
            Audio lives in <code className="font-mono">{saving.this_process.directory}</code> —{' '}
            {formatNumber(saving.this_process.disk_files)} files,{' '}
            {formatBytes(saving.this_process.disk_bytes)}. Set{' '}
            <code className="font-mono">GLEE_TTS_CACHE_DIR</code> to somewhere persistent:
            a temp directory is cleared on boot, which turns a cache into a warm-up.
          </p>
        </Card>
      )}

      <Card
        title="Cached lines"
        description="Ordered by how often each has been played instead of bought."
      >
        <form
          className="mb-4 flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setQuery(search.trim());
          }}
        >
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search what the bot said…"
            className="max-w-md"
          />
          <Button type="submit" variant="secondary">
            Search
          </Button>
          {query && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSearch('');
                setQuery('');
              }}
            >
              Clear
            </Button>
          )}
        </form>

        {entries.isPending ? (
          <Loading label="Reading the cache…" />
        ) : entries.error ? (
          <ErrorState error={entries.error} retry={() => entries.refetch()} />
        ) : !entries.data?.data.length ? (
          <EmptyState
            title={query ? 'Nothing matches that' : 'Nothing cached yet'}
            description={
              query
                ? 'The bot has not said those words, in any voice.'
                : 'Run glee-warm-cache to pre-generate every line, or make a call and it will fill itself.'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="glee-table">
              <thead>
                <tr>
                  <th>Line</th>
                  {/* Who bought it FIRST, not who owns it. Entries are
                      shared across every client on the same provider account,
                      so a line here is very often being played on calls for
                      accounts other than the one named. */}
                  <th title="The client whose call first paid for this line. The entry is shared with every other client on the same provider account.">
                    First bought by
                  </th>
                  <th>Voice</th>
                  <th className="text-right">Played</th>
                  <th className="text-right">Size</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {entries.data.data.map((entry) => (
                  <tr key={entry.cache_key}>
                    <td className="max-w-md">
                      <div className="truncate" title={entry.text}>
                        {entry.text}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-500">
                        {entry.static && <Badge tone="blue">pre-generated</Badge>}
                        <span>{(entry.duration_ms / 1000).toFixed(1)}s</span>
                        <span>{entry.characters} chars</span>
                        {entry.last_used_at && (
                          <span>last {formatDateTime(entry.last_used_at)}</span>
                        )}
                      </div>
                    </td>
                    <td className="text-ink-600 dark:text-ink-300">
                      {entry.client_name ?? 'platform'}
                    </td>
                    <td className="text-xs text-ink-500">
                      {entry.voice_id || 'default'}
                      <div>{entry.provider}</div>
                    </td>
                    <td className="text-right tabular-nums">
                      {formatNumber(entry.hits)}
                    </td>
                    <td className="text-right tabular-nums text-xs text-ink-500">
                      {formatBytes(entry.bytes)}
                    </td>
                    <td>
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => play(entry.cache_key)}
                          disabled={playing === entry.cache_key}
                        >
                          {playing === entry.cache_key ? 'Playing…' : 'Play'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => regenerate.mutate(entry.cache_key)}
                          disabled={regenerate.isPending}
                        >
                          Regenerate
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => remove.mutate(entry.cache_key)}
                          disabled={remove.isPending}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
